import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';
import { Tournament } from '@features/shared/api/typings';
import { useTournamentParticipantsQuery } from '@features/tournament/api/hooks';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';
import { ParticipantsModal } from '@features/tournament/ui/components/ParticipantsModal';
import {
  fixBrokenPositionsForParticipants,
  sortParticipantsByTeamAndPosition,
} from '@features/tournament/ui/components/TournamentParticipantsEdit/components/participantsHelpers';
import { useModal } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/useModal';
import { useMutations } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/useMutations';
import { TournamentParticipantsEdit } from '@features/tournament/ui/components/TournamentParticipantsEdit/TournamentParticipantsEdit';
import { ParticipantsTab } from '@features/tournament/ui/components/TournamentParticipantsEdit/types';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings';
import { useRcfUsersQuery } from '@features/user/api/hooks';
import { RcfUserSearchContext } from '@features/user/ui/RcfUserSearch/RcfUserSearchContext';

export type Props = {
  tournament: Tournament;
  tab: ParticipantsTab;
  onTab: (newTab: ParticipantsTab) => void;
};

export function TournamentParticipantsEditWidget({
  tournament: initialTournament,
  tab,
  onTab,
}: Props) {
  // TODO подумать как избавиться от этого г. Обновленный турнир в контекст?
  const { getValues } = useFormContext<TournamentEditFormValues>();
  const mode = getValues('mode');
  const isTeam = mode === 'Team' || mode === 'PrivateTeam' || mode === 'TwoOnTwo';
  const teamModeSettings = getValues('teamModeSettings');
  const clockControl = getValues('clockControl');
  const tournament = useMemo(
    () => ({
      ...initialTournament,
      mode,
      clockControl,
      teamModeSettings,
    }),
    [initialTournament, mode, teamModeSettings, clockControl],
  );

  const [query, setQuery] = useState('');
  const {
    data: rcfUsers,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useRcfUsersQuery({
    query,
    limit: 100,
  });

  const { clubId, tournamentId } = tournament;

  const { isEditable, isModeration, isFinished } = useTournamentStates(tournament);

  const {
    data,
    refetch: refetchParticipants,
    isPending,
  } = useTournamentParticipantsQuery(clubId, tournamentId, tab, 'NumberAsc');

  const { modalProps, modalVisible, hideModal, showModal } = useModal(
    clubId,
    tournamentId,
    refetchParticipants,
    tab,
  );

  const mutations = useMutations(
    clubId,
    tournamentId,
    tab,
    isModeration,
    isEditable,
    isFinished,
    isTeam,
  );

  const isAdmin = useIsAdmin();

  fixBrokenPositionsForParticipants(data);

  sortParticipantsByTeamAndPosition(
    data,
    tournament.teams?.map((t) => t.teamId),
  );

  return (
    <RcfUserSearchContext.Provider
      value={{
        query,
        setQuery,
        rcfUsers: query && rcfUsers ? rcfUsers : [],
        isLoading: isFetching,
        hasNextPage,
        fetchNextPage,
      }}
    >
      <TournamentParticipantsEdit
        tournament={tournament}
        participants={data}
        isPending={isPending}
        tab={tab}
        onTabChange={onTab}
        showModal={showModal}
        modal={
          <ParticipantsModal
            {...modalProps}
            isOpen={modalVisible}
            tournament={tournament}
            hide={hideModal}
          />
        }
        {...mutations}
        isRcf={isAdmin && tournament.rcfRankCalculated}
      />
    </RcfUserSearchContext.Provider>
  );
}
